import React, { useEffect, useMemo } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useCreateStoreAddressMutation,
  useGetStoreMyProfileQuery,
  useUpdateStoreAddressMutation,
} from "../redux/services/store/storeAccountApi";
import { useGetStoreMeQuery } from "../redux/services/store/storeAuthApi";
import { useGetStoreShippingCountriesQuery } from "../redux/services/store/storeShippingApi";

// delete modal
const DeleteModal = ({ removeModel, hideModal, deleteData }: any) => {
  const handleDelete = () => {
    deleteData();
    hideModal();
  };

  return (
    <Modal show={removeModel} onHide={hideModal} centered id="removeItemModal" className="zoomIn">
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body>
        <div className="mt-2 text-center">
          <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
            <h4>Are you sure ?</h4>
            <p className="text-muted mx-4 mb-0">Are you sure you want to remove this product ?</p>
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
          <Button className="btn w-sm btn-light" data-bs-dismiss="modal" onClick={hideModal}>
            Close
          </Button>
          <Button className="btn w-sm btn-danger" id="remove-product" onClick={handleDelete}>
            Yes, Delete It!
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DeleteModal;

// add / edit address modal
export const ModalAdd = ({
  addressModal,
  handleClose,
  editAddress,
  defaultAddressType = "shipping",
}: any) => {
  const [createStoreAddress, { isLoading: isCreating }] = useCreateStoreAddressMutation();
  const [updateStoreAddress, { isLoading: isUpdating }] = useUpdateStoreAddressMutation();
  const { data: me } = useGetStoreMeQuery();
  const { data: profile } = useGetStoreMyProfileQuery();
  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    isFetching: isCountriesFetching,
  } = useGetStoreShippingCountriesQuery();

  const isEdit = !!editAddress?.id;
  const busy = isCreating || isUpdating;

  const INDIA_STATES = useMemo(
    () => [
      "Andhra Pradesh",
      "Arunachal Pradesh",
      "Assam",
      "Bihar",
      "Chhattisgarh",
      "Goa",
      "Gujarat",
      "Haryana",
      "Himachal Pradesh",
      "Jharkhand",
      "Karnataka",
      "Kerala",
      "Madhya Pradesh",
      "Maharashtra",
      "Manipur",
      "Meghalaya",
      "Mizoram",
      "Nagaland",
      "Odisha",
      "Punjab",
      "Rajasthan",
      "Sikkim",
      "Tamil Nadu",
      "Telangana",
      "Tripura",
      "Uttar Pradesh",
      "Uttarakhand",
      "West Bengal",
      "Andaman and Nicobar Islands",
      "Chandigarh",
      "Dadra and Nagar Haveli and Daman and Diu",
      "Delhi",
      "Jammu and Kashmir",
      "Ladakh",
      "Lakshadweep",
      "Puducherry",
    ],
    []
  );

  const countries = useMemo(() => {
    const rows = Array.isArray(countriesData) ? countriesData : [];
    const selectedCode = String(editAddress?.country_code || "IN").toUpperCase();

    if (!rows.length) {
      return [{ id: 0, iso2_code: selectedCode, name: selectedCode }];
    }

    const exists = rows.some((c) => String(c.iso2_code).toUpperCase() === selectedCode);
    if (exists) return rows;

    return [
      ...rows,
      {
        id: 0,
        iso2_code: selectedCode,
        iso3_code: null,
        name: selectedCode,
        phone_code: null,
        currency_code: null,
        is_active: true,
        sort_order: 999999,
        created_at: null,
        updated_at: null,
      },
    ];
  }, [countriesData, editAddress]);

  const defaultContactName = useMemo(() => {
    const fullName = String(profile?.full_name || "").trim();
    if (fullName) return fullName;

    const joinedName = [profile?.first_name, profile?.last_name]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();

    return joinedName;
  }, [profile?.first_name, profile?.full_name, profile?.last_name]);

  const defaultMobile = useMemo(() => {
    return String(me?.mobile_e164 || "").trim();
  }, [me?.mobile_e164]);

  const formik = useFormik({
    initialValues: {
      contact_name: "",
      mobile_e164: "",
      line1: "",
      line2: "",
      landmark: "",
      city: "",
      state: "",
      postal_code: "",
      country_code: "IN",
      address_type: defaultAddressType,
      label: "",
      make_default_shipping: true,
      make_default_billing: true,
    },
    validationSchema: Yup.object({
      contact_name: Yup.string().required("Please Enter Your Name"),
      mobile_e164: Yup.string().required("Please Enter Your Phone"),
      line1: Yup.string().required("Please Enter Address Line 1"),
      line2: Yup.string().nullable(),
      landmark: Yup.string().nullable(),
      city: Yup.string().required("Please Enter City"),
      state: Yup.string().required("Please Enter State"),
      postal_code: Yup.string().required("Please Enter Postal Code"),
      country_code: Yup.string().required("Please Select Country").length(2, "Use 2 letter country code"),
      address_type: Yup.string().oneOf(["shipping", "billing", "other"]).required("Please Select Address Type"),
      label: Yup.string().nullable(),
      make_default_shipping: Yup.boolean(),
      make_default_billing: Yup.boolean(),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          address_type: values.address_type as "shipping" | "billing" | "other",
          label: values.label || null,
          contact_name: values.contact_name.trim(),
          mobile_e164: values.mobile_e164.trim(),
          line1: values.line1.trim(),
          line2: values.line2?.trim() || null,
          landmark: values.landmark?.trim() || null,
          city: values.city.trim(),
          state: values.state.trim(),
          postal_code: values.postal_code.trim(),
          country_code: values.country_code.trim().toUpperCase(),
          make_default_shipping: values.make_default_shipping,
          make_default_billing: values.make_default_billing,
        };

        if (isEdit) {
          await updateStoreAddress({
            address_id: Number(editAddress.id),
            body: payload,
          }).unwrap();
        } else {
          await createStoreAddress(payload).unwrap();
        }

        resetForm();
        handleClose();
      } catch (e) {
        console.log(e);
      }
    },
  });

  useEffect(() => {
    if (addressModal) {
      if (isEdit) {
        formik.setValues({
          contact_name: editAddress?.contact_name || "",
          mobile_e164: editAddress?.mobile_e164 || "",
          line1: editAddress?.line1 || "",
          line2: editAddress?.line2 || "",
          landmark: editAddress?.landmark || "",
          city: editAddress?.city || "",
          state: editAddress?.state || "",
          postal_code: editAddress?.postal_code || "",
          country_code: String(editAddress?.country_code || "IN").toUpperCase(),
          address_type: editAddress?.address_type || defaultAddressType,
          label: editAddress?.label || "",
          make_default_shipping: !!editAddress?.is_default_shipping,
          make_default_billing: !!editAddress?.is_default_billing,
        });
      } else {
        formik.setValues({
          contact_name: defaultContactName,
          mobile_e164: defaultMobile,
          line1: "",
          line2: "",
          landmark: "",
          city: "",
          state: "",
          postal_code: "",
          country_code: "IN",
          address_type: defaultAddressType,
          label: "",
          make_default_shipping: true,
          make_default_billing: true,
        });
      }
    } else {
      formik.setValues({
        contact_name: defaultContactName,
        mobile_e164: defaultMobile,
        line1: "",
        line2: "",
        landmark: "",
        city: "",
        state: "",
        postal_code: "",
        country_code: "IN",
        address_type: defaultAddressType,
        label: "",
        make_default_shipping: true,
        make_default_billing: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressModal, editAddress, defaultAddressType, defaultContactName, defaultMobile]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextCountry = String(e.target.value || "").toUpperCase();
    const prevCountry = String(formik.values.country_code || "").toUpperCase();

    formik.setFieldValue("country_code", nextCountry);

    if (prevCountry !== nextCountry) {
      formik.setFieldValue("state", "");
    }
  };

  const isIndia = String(formik.values.country_code || "").toUpperCase() === "IN";
  const countriesBusy = isCountriesLoading || isCountriesFetching;

  return (
    <React.Fragment>
      <Modal show={addressModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <h1 className="modal-title fs-5" id="addAddressModalLabel">
            {isEdit ? "Edit Address" : "Add New Address"}
          </h1>
        </Modal.Header>
        <Modal.Body>
          <Form className="needs-validation createAddress-form" id="createAddress-form" onSubmit={formik.handleSubmit}>
            <div>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-name">
                      Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="addaddress-name"
                      placeholder="Enter name"
                      name="contact_name"
                      value={formik.values.contact_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.errors.contact_name && formik.touched.contact_name ? (
                      <span className="text-danger">{formik.errors.contact_name}</span>
                    ) : null}
                  </div>
                </Col>

                <Col md={6}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-phone">
                      Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="addaddress-phone"
                      placeholder="Enter phone no."
                      name="mobile_e164"
                      value={formik.values.mobile_e164}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.errors.mobile_e164 && formik.touched.mobile_e164 ? (
                      <span className="text-danger">{formik.errors.mobile_e164}</span>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-label">Label</Form.Label>
                    <Form.Control
                      type="text"
                      id="addaddress-label"
                      placeholder="Home / Office / Other"
                      name="label"
                      value={formik.values.label}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </Col>

                <Col md={6}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-type">
                      Address Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      id="addaddress-type"
                      name="address_type"
                      value={formik.values.address_type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="shipping">Shipping</option>
                      <option value="billing">Billing</option>
                      <option value="other">Other</option>
                    </Form.Select>
                    {formik.errors.address_type && formik.touched.address_type ? (
                      <span className="text-danger">{String(formik.errors.address_type)}</span>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <div className="mb-3">
                <Form.Label htmlFor="addaddress-line1">
                  Address Line 1 <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  id="addaddress-line1"
                  placeholder="Enter address line 1"
                  name="line1"
                  value={formik.values.line1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.errors.line1 && formik.touched.line1 ? (
                  <span className="text-danger">{formik.errors.line1}</span>
                ) : null}
              </div>

              <div className="mb-3">
                <Form.Label htmlFor="addaddress-line2">Address Line 2</Form.Label>
                <Form.Control
                  type="text"
                  id="addaddress-line2"
                  placeholder="Enter address line 2"
                  name="line2"
                  value={formik.values.line2}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Form.Label htmlFor="addaddress-landmark">Landmark</Form.Label>
                <Form.Control
                  type="text"
                  id="addaddress-landmark"
                  placeholder="Enter landmark"
                  name="landmark"
                  value={formik.values.landmark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-city">
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="addaddress-city"
                      placeholder="Enter city"
                      name="city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.errors.city && formik.touched.city ? (
                      <span className="text-danger">{formik.errors.city}</span>
                    ) : null}
                  </div>
                </Col>

                <Col md={4}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-state">
                      State <span className="text-danger">*</span>
                    </Form.Label>

                    {isIndia ? (
                      <Form.Select
                        id="addaddress-state"
                        name="state"
                        value={formik.values.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      >
                        <option value="">Select state</option>
                        {INDIA_STATES.map((stateName) => (
                          <option key={stateName} value={stateName}>
                            {stateName}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type="text"
                        id="addaddress-state"
                        placeholder="Enter state / province / region"
                        name="state"
                        value={formik.values.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    )}

                    {formik.errors.state && formik.touched.state ? (
                      <span className="text-danger">{formik.errors.state}</span>
                    ) : null}
                  </div>
                </Col>

                <Col md={4}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-postal">
                      Postal Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="addaddress-postal"
                      placeholder="Enter postal code"
                      name="postal_code"
                      value={formik.values.postal_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.errors.postal_code && formik.touched.postal_code ? (
                      <span className="text-danger">{formik.errors.postal_code}</span>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Form.Label htmlFor="addaddress-country">
                      Country <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      id="addaddress-country"
                      name="country_code"
                      value={formik.values.country_code}
                      onChange={handleCountryChange}
                      onBlur={formik.handleBlur}
                      disabled={countriesBusy}
                    >
                      {countriesBusy ? (
                        <option value={formik.values.country_code}>Loading countries...</option>
                      ) : (
                        countries.map((country: any) => (
                          <option key={country.iso2_code} value={country.iso2_code}>
                            {country.name}
                          </option>
                        ))
                      )}
                    </Form.Select>
                    {countriesBusy ? (
                      <div className="mt-1 text-muted fs-12">
                        <Spinner animation="border" size="sm" className="me-1" />
                        Loading country list...
                      </div>
                    ) : null}
                    {formik.errors.country_code && formik.touched.country_code ? (
                      <span className="text-danger">{formik.errors.country_code}</span>
                    ) : null}
                  </div>
                </Col>

                <Col md={6} className="d-flex align-items-center">
                  <div className="w-100">
                    <Form.Check
                      type="checkbox"
                      id="make_default_shipping"
                      name="make_default_shipping"
                      label="Make default shipping"
                      checked={formik.values.make_default_shipping}
                      onChange={formik.handleChange}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      id="make_default_billing"
                      name="make_default_billing"
                      label="Make default billing"
                      checked={formik.values.make_default_billing}
                      onChange={formik.handleChange}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button className="btn btn-light" data-bs-dismiss="modal" onClick={handleClose} disabled={busy}>
                Close
              </Button>
              <Button type="submit" id="addNewAddress" className="btn btn-primary" disabled={busy || countriesBusy}>
                {busy ? "Saving..." : isEdit ? "Update" : "Add"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};
